import { API_PLACEMENT } from '@constants';
import axios from 'axios';
import { AuthorizationService } from '@services/auth';
import { handleServiceError } from '@utils';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';

export interface PlacementCheckResponse {
  canBePlaced: boolean;
  instruction: string;
}

export interface PlacementGenerateResponse {
  imageId: string;
  imageBase64: string;
  responseText: string;
  videoOperationName?: string;
}

export interface VideoStatusResponse {
  operationName: string;
  status: string;
  videoBase64?: string;
  errorMessage?: string;
}

export class PlacementService extends AuthorizationService {
  static readonly serviceName: string = 'PlacementService';

  public async checkPlacement({
    productId,
    tenantKey
  }: {
    productId: string;
    tenantKey?: string;
  }): Promise<PlacementCheckResponse | null> {
    try {
      const response = await axios.get(`/check/${productId}`, {
        baseURL: API_PLACEMENT,
        headers:
          isFeatureEnabled(FEATURES.MULTI_TENANCY) && tenantKey
            ? { 'volcano-tenant-key': tenantKey }
            : {}
      });

      return response.data;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async generatePlacement({
    sceneImage,
    productId,
    notes,
    generateVideo,
    tenantKey
  }: {
    sceneImage: File | Blob;
    productId: string;
    notes?: string;
    generateVideo?: boolean;
    tenantKey?: string;
  }): Promise<PlacementGenerateResponse | null> {
    try {
      const formData = new FormData();
      formData.append('sceneImage', sceneImage);
      formData.append('productId', productId);
      if (generateVideo) formData.append('generateVideo', 'true');
      if (notes) formData.append('inputNotes', notes);

      const headers: Record<string, string> = {
        'Content-Type': 'multipart/form-data'
      };

      if (isFeatureEnabled(FEATURES.MULTI_TENANCY) && tenantKey) {
        headers['volcano-tenant-key'] = tenantKey;
      }

      const response = await axios.post('/generate', formData, {
        baseURL: API_PLACEMENT,
        headers
      });

      return response.data;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async editPlacement({
    imageId,
    editingPrompt,
    tenantKey
  }: {
    imageId: string;
    editingPrompt: string;
    tenantKey?: string;
  }): Promise<PlacementGenerateResponse | null> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (isFeatureEnabled(FEATURES.MULTI_TENANCY) && tenantKey) {
        headers['volcano-tenant-key'] = tenantKey;
      }

      const response = await axios.post(
        '/edit',
        { imageId, editingPrompt },
        {
          baseURL: API_PLACEMENT,
          headers
        }
      );

      return response.data;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getVideoStatus({
    operationName,
    tenantKey
  }: {
    operationName: string;
    tenantKey?: string;
  }): Promise<VideoStatusResponse | null> {
    try {
      const headers: Record<string, string> = {};

      if (isFeatureEnabled(FEATURES.MULTI_TENANCY) && tenantKey) {
        headers['volcano-tenant-key'] = tenantKey;
      }

      const response = await axios.get('/video', {
        baseURL: API_PLACEMENT,
        headers,
        params: { operationName }
      });

      return response.data;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }
}
